import { useEffect, useState } from "react";
import { useAuth } from "./useAuth";
import { useUserStatsContext } from "../contexts/UserStatsContext";
import {
  emptyUserStats,
  subscribeToUserStats,
  type UserStats,
} from "../services/firebase/statsService";

/**
 * Estatísticas do utilizador `uid`.
 * Para o próprio utilizador logado, reutiliza o contexto (subscrição global).
 * Para outro uid (ex.: perfil público), subscreve `user_stats/{uid}`.
 */
export function useUserStats(uid: string | undefined): { stats: UserStats; isLoading: boolean } {
  const { user } = useAuth();
  const ctx = useUserStatsContext();
  const [remoteStats, setRemoteStats] = useState<UserStats>(emptyUserStats());
  const [remoteLoading, setRemoteLoading] = useState(false);

  const selfUid = user?.uid;
  const isSelf = uid != null && selfUid != null && uid === selfUid;

  useEffect(() => {
    if (uid == null) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reset sincrono pre-subscription (padrao anterior ao react-hooks v7); refactor de timing rastreado na auditoria 03/08 par.4.1
      setRemoteLoading(false);
      return;
    }
    if (isSelf) {
      setRemoteLoading(false);
      return;
    }

    setRemoteLoading(true);
    const unsub = subscribeToUserStats(
      uid,
      (next) => {
        setRemoteStats(next);
        setRemoteLoading(false);
      },
      () => {
        setRemoteStats(emptyUserStats());
        setRemoteLoading(false);
      },
    );
    return unsub;
  }, [uid, isSelf]);

  if (uid == null) {
    return { stats: emptyUserStats(), isLoading: true };
  }

  if (isSelf) {
    return { stats: ctx.stats, isLoading: ctx.isLoading };
  }

  return { stats: remoteStats, isLoading: remoteLoading };
}
